import React, { useState, useEffect } from 'react';
import checkIcon from '../assets/check_icon.png';
import removeIcon from '../assets/remove_icon.png';
import config from '../config';
import { getUserIdFromUrl } from '../helper/apiHelper';
import { useTranslation } from '../hooks/useTranslation';
import { useStatic } from '../context/StaticContext';
import { CircularProgress } from '@mui/material';

const HistoryPopup = ({ isOpen, onClose }) => {
  const [historyData, setHistoryData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { t } = useTranslation();
  const { selectedLanguage, isLanguage } = useStatic();
  const userId = getUserIdFromUrl();

  // Çarka yönlendirme fonksiyonu
  const handleNavigate = (subdomain) => {
    const baseHostname = window.location.hostname; // Örn: sterlinbetbonus.xyz
    const newUrl = `https://${subdomain}.${baseHostname}/${userId}`;
    window.location.href = newUrl;
  };

  // Dinamik çeviri fonksiyonu (MyMemory API kullanarak)
  const translateText = async (text, targetLang) => {
    if (!isLanguage || !targetLang || targetLang === 'tr-TR') {
      return text; // Türkçe ise çevirme
    }

    try {
      // Dil kodunu API formatına çevir (tr-TR -> tr|en)
      const langMap = {
        'en-US': 'tr|en',
        'de-DE': 'tr|de', 
        'fr-FR': 'tr|fr',
        'es-ES': 'tr|es',
        'it-IT': 'tr|it',
        'pt-PT': 'tr|pt'
      };

      const langPair = langMap[targetLang];
      if (!langPair) return text;

      const response = await fetch(
        `https://api.mymemory.translated.net/get?q=${encodeURIComponent(text)}&langpair=${langPair}`
      );
      
      const data = await response.json();
      
      if (data.responseStatus === 200 && data.responseData.translatedText) {
        return data.responseData.translatedText;
      }
      
      return text; // Çeviri başarısızsa orijinal metni döndür
    } catch (error) {
      console.warn('Translation failed:', error);
      return text; // Hata durumunda orijinal metni döndür
    }
  };

  // Tarih formatını dile göre ayarla
  const formatDate = (dateString, lang) => {
    const date = new Date(dateString);
    
    const locales = {
      'tr-TR': 'tr-TR',
      'en-US': 'en-US',
      'de-DE': 'de-DE',
      'fr-FR': 'fr-FR',
      'es-ES': 'es-ES',
      'it-IT': 'it-IT',
      'pt-PT': 'pt-PT'
    };

    const locale = locales[lang] || 'tr-TR';

    const day = date.getDate();
    const month = date.toLocaleString(locale, { month: 'long' });
    const time = date.toLocaleString(locale, { 
      hour: '2-digit', 
      minute: '2-digit', 
      second: '2-digit',
      hour12: false
    });

    return { day, month, time };
  };

  useEffect(() => {
    // Popup açıldığında geçmiş verilerini çek
    if (isOpen) {
      fetchHistoryData();
    }
  }, [isOpen]);

  // Dil değiştiğinde geçmişi yeniden yükle
  useEffect(() => {
    if (isOpen && isLanguage) {
      fetchHistoryData();
    }
  }, [selectedLanguage, isOpen, isLanguage]);

  const fetchHistoryData = async () => {
    setLoading(true);
    setError(null);

    try {
      let body = JSON.stringify({
        process_id: 'user-info',
        user_id: getUserIdFromUrl(),
        recaptcha: ""
      });
      let keys = await window.rh.cr(body);
      const response = await fetch(`${config.apiBaseUrl}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          "hk": keys[0],
          "hh": keys[1]
        },
        body: body,
      });

      const data = await response.json();

      if (data.status && data.bonus) {
        // API yanıtındaki bonus geçmişi verisini işle
        const formattedDataPromises = data.bonus.map(async (item) => {
          // Tarih/saat bilgisini seçili dile göre formatla
          const { day, month, time } = formatDate(item.date, selectedLanguage);
          
          // Durum bilgisini formatla
          const status = item.status === 'Onaylandı' ? 'success' : 'error';

          // item.notes'u dinamik olarak çevir
          const translatedMessage = await translateText(item.notes, selectedLanguage);
          
          return {
            date: `${day} ${month}`,
            time: time,
            title: item.bonus_name,
            status: status,
            message: translatedMessage,
            subdomain: item.subdomain, // Subdomain bilgisini koru
            originalStatus: item.status // Orijinal status bilgisini koru
          };
        });

        // Tüm çeviriler tamamlandığında state'i güncelle
        const formattedData = await Promise.all(formattedDataPromises);
        setHistoryData(formattedData);
      } else {
        setError('Geçmiş verileri alınamadı.');
      }
    } catch (err) {
      console.error('Talep geçmişi alınırken hata oluştu:', err);
      setError('Geçmiş verileri alınırken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="history-popup-overlay">
      <div className="history-popup">
        <div className="history-popup-header">
          <h2>{t('popup.historyTitle', 'Talep Geçmişi')}</h2>
          <button 
            className="close-button"
            onClick={onClose}
          >
            ✕
          </button>
        </div>
        
        <div className="history-items-container">
          <div className="history-items">
            {loading ? (
              <div className="history-loading">
                <CircularProgress 
                  size={40}
                  thickness={4}
                  sx={{
                    color: '#1475E1',
                    '& .MuiCircularProgress-circle': {
                      strokeLinecap: 'round',
                    },
                  }}
                />
                <p>{t('popup.loadingHistory', 'Geçmiş talepleri yükleniyor...')}</p>
              </div>
            ) : error ? (
              <div className="history-error">{error}</div>
            ) : historyData.length === 0 ? (
              <div className="history-empty">{t('popup.noHistory', 'Henüz talep geçmişiniz bulunmamaktadır.')}</div>
            ) : (
              historyData.map((item, index) => (
                <div key={index} className="history-item">
                  <div className="history-date">
                    <div>{item.date}</div>
                    <div>{item.time}</div>
                  </div>
                  
                  <div className="history-content">
                    <h3>{item.title}</h3>
                    <p className={`history-message ${item.status}`}>
                      {item.status === 'success' ? (
                        <img src={checkIcon} alt="Başarılı" className="status-icon" />
                      ) : (
                        <img src={removeIcon} alt="Hata" className="status-icon" />
                      )}
                      {item.message}
                    </p>
                    
                  </div>
                  {item.subdomain && item.originalStatus === "Onaylandı" && (
                      <button 
                        className="navigate-wheel-button" 
                        onClick={() => handleNavigate(item.subdomain)}
                      >
                        {t('buttons.goToWheel', 'Çarka Git')}
                      </button>
                    )}
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HistoryPopup; 